import React, { ReactNode, useState } from 'react'

import { SearchField, Select } from '@octane/spark'
import { FieldProps, Field, getIn } from 'formik'

import { ErrorMessage, Legend, Container } from './FormikDropdown.styled'

export interface FormikDropdownChild {
    name: string | JSX.Element
    value: string
}

type FormikSelectProps = {
    name: string
    label: string
    placeholder?: string
    info?: ReactNode
    infoText?: ReactNode
    children: FormikDropdownChild[]
    disabled?: boolean
    prefilled?: boolean
    error?: boolean
    searchable?: boolean
    searchPlaceholder?: string
    noMatchComponent?: ReactNode
    onChange?: (value: string) => void
    tabIndex?: number
}

const filterChildren = (
    children: FormikDropdownChild[],
    searchPhrase: string,
) => {
    return children.filter((child) => {
        if (searchPhrase && typeof child.name === 'string') {
            const nameMatch = child.name
                ?.toLowerCase()
                ?.includes(searchPhrase.toLowerCase())

            const valueMatch = child.value
                ?.toLowerCase()
                ?.includes(searchPhrase.toLowerCase())

            return nameMatch || valueMatch
        }

        return true
    })
}

function FormikDropdown({
    name,
    placeholder = 'Select',
    label,
    children,
    info = '',
    infoText,
    disabled = false,
    onChange = () => undefined as void,
    error = false,
    searchable = false,
    searchPlaceholder = 'Search...',
    noMatchComponent = undefined,
    prefilled,
    tabIndex,
}: Readonly<FormikSelectProps>) {
    const [searchPhrase, setSearchPhrase] = useState('')

    return (
        <Container>
            <Field name={name}>
                {({ field, form }: FieldProps) => {
                    const touched = getIn(form.touched, field.name)
                    const formikErrorMessage = getIn(form.errors, field.name)
                    const hasError = !!(formikErrorMessage || error)

                    return (
                        <>
                            {info && (
                                <Legend withIcon={!!infoText}>{info}</Legend>
                            )}
                            <Select
                                disabled={disabled}
                                prefilled={prefilled}
                                label={label}
                                value={String(field.value)}
                                placeholder={placeholder}
                                id={field.name}
                                info={infoText}
                                data-testid={name}
                                error={!!(touched && hasError)}
                                tabIndex={tabIndex}
                                onChange={(val: string) => {
                                    const event = {
                                        target: {
                                            name,
                                            value: val,
                                        },
                                    }

                                    form.handleChange(event)
                                    onChange(val)
                                }}
                            >
                                {searchable && (
                                    <Select.Search key="searchComponent">
                                        <SearchField
                                            size="small"
                                            value={searchPhrase}
                                            onChange={(value: string) =>
                                                setSearchPhrase(value)
                                            }
                                            placeholder={searchPlaceholder}
                                            onClear={() => setSearchPhrase('')}
                                        />
                                    </Select.Search>
                                )}
                                {searchable && noMatchComponent && (
                                    <Select.NoMatch key="noMatchComponent">
                                        {noMatchComponent}
                                    </Select.NoMatch>
                                )}
                                {filterChildren(
                                    children || [],
                                    searchPhrase,
                                ).map((child) => (
                                    <Select.Option
                                        value={child.value}
                                        key={child.value}
                                        data-testid={child.value}
                                    >
                                        {child.name}
                                    </Select.Option>
                                ))}
                            </Select>{' '}
                            {!!(touched && formikErrorMessage) && (
                                <ErrorMessage>
                                    {formikErrorMessage}
                                </ErrorMessage>
                            )}
                        </>
                    )
                }}
            </Field>
        </Container>
    )
}

export default FormikDropdown
